import { FormEvent, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import 'react-datepicker/dist/react-datepicker.css';
import './App.css';
import './vitegoAdmin.css';
import './adminRedesign.css';
import './workspacePalette.css';
import './workspaceComponents.css';
import './workspaceDesign.css';
import { ApiClientError, setHttpToken } from './api/httpClient';
import { AdminBootstrapDictionaries, AuthToken, LoginRequest } from './api/types';
import { adminApi } from './modules/admin/adminApi';
import { AdminWorkspace } from './modules/admin/AdminWorkspace';
import { AdminLoadingPage } from './modules/admin/pages/AdminLoadingPage';
import { AdminLoginPage } from './modules/admin/pages/AdminLoginPage';
import { readAdminTheme, saveAdminTheme } from './modules/admin/theme';
import { authApi } from './modules/auth/authApi';
import { clearAuthToken, readAuthToken, saveAuthToken, subscribeToAuthTokenChanges } from './modules/auth/authStorage';
import {
  addFormError,
  FormErrors,
  formErrorsFromApiError,
  hasFormErrors,
  validateEmail,
} from './utils/formValidation';

type AuthState = 'checking' | 'guest' | 'admin';

const defaultLoginForm: LoginRequest = {
  user_email: '',
  password: '',
};

export default function App() {
  return (
    <BrowserRouter>
      <AdminAppContent />
    </BrowserRouter>
  );
}

function AdminAppContent() {
  const [storedToken, setStoredToken] = useState(readAuthToken);
  const [theme, setTheme] = useState(() => readAdminTheme());
  const [authToken, setAuthToken] = useState<AuthToken | null>(() => storedToken);
  const [authState, setAuthState] = useState<AuthState>(() => (storedToken === null ? 'guest' : 'checking'));
  const [dictionaries, setDictionaries] = useState<AdminBootstrapDictionaries | null>(null);
  const [loginForm, setLoginForm] = useState<LoginRequest>(defaultLoginForm);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginFieldErrors, setLoginFieldErrors] = useState<FormErrors>({});
  const loginInFlight = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.palette = 'vitego';
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;

    const documentStyles = getComputedStyle(document.documentElement);
    const browserThemeColor = documentStyles.getPropertyValue('--admin-background').trim();
    const browserThemeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    if (browserThemeColorMeta !== null && browserThemeColor !== '') {
      browserThemeColorMeta.content = browserThemeColor;
    }
  }, [theme]);

  useEffect(() => {
    if (storedToken === null) {
      return;
    }

    let isActive = true;
    setHttpToken(storedToken.access_token);

    const currentUserPromise = authApi.me();
    const dictionariesPromise = adminApi.getBootstrapDictionaries();

    Promise.all([currentUserPromise, dictionariesPromise])
      .then(([currentUser, bootstrapDictionaries]) => {
        if (!isActive) return;
        const nextAuthToken: AuthToken = {
          ...storedToken,
          user: currentUser,
        };

        saveAuthToken(nextAuthToken);
        setAuthToken(nextAuthToken);
        setDictionaries(bootstrapDictionaries);
        setAuthState('admin');
      })
      .catch(() => {
        if (!isActive) return;
        clearAdminSession();
        setAuthToken(null);
        setDictionaries(null);
        setAuthState('guest');
      });
    return () => { isActive = false; };
  }, [storedToken]);

  useEffect(() => {
    const unsubscribe = subscribeToAuthTokenChanges((token) => {
      if (token?.access_token === authToken?.access_token) return;
      setHttpToken(token?.access_token ?? null);
      setStoredToken(token);
      setAuthToken(token);
      setDictionaries(null);
      setAuthState(token === null ? 'guest' : 'checking');
    });

    return unsubscribe;
  }, [authToken?.access_token]);

  const handleLogin = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (loginInFlight.current) {
      return;
    }

    setLoginError(null);
    const validationErrors = validateAdminLogin(loginForm);

    setLoginFieldErrors(validationErrors);

    if (hasFormErrors(validationErrors)) {
      return;
    }

    loginInFlight.current = true;
    setIsSubmitting(true);

    authApi
      .login(loginForm)
      .then((nextAuthToken) => {
        saveAuthToken(nextAuthToken);
        setHttpToken(nextAuthToken.access_token);
        setAuthToken(nextAuthToken);
        setLoginForm(defaultLoginForm);
        setLoginFieldErrors({});

        const dictionariesPromise = adminApi.getBootstrapDictionaries();

        return dictionariesPromise.then((bootstrapDictionaries) => {
          const currentToken = readAuthToken();
          if (currentToken?.access_token !== nextAuthToken.access_token) return;
          setDictionaries(bootstrapDictionaries);
          setAuthState('admin');

          return undefined;
        });
      })
      .catch((error: unknown) => {
        const apiErrors = formErrorsFromApiError(error);
        const message = error instanceof ApiClientError ? error.message : 'Не удалось выполнить вход.';

        setLoginFieldErrors(apiErrors);
        setLoginError(message);
      })
      .finally(() => {
        loginInFlight.current = false;
        setIsSubmitting(false);
      });
  };

  const handleLogout = (): void => {
    const logoutPromise = authApi.logout();
    clearAdminSession();
    setStoredToken(null);
    setAuthToken(null);
    setDictionaries(null);
    setAuthState('guest');

    void logoutPromise.catch(() => {
      // Локальная сессия уже завершена. Ошибка удалённого logout не должна удерживать администратора в ЛК.
    });
  };

  const toggleTheme = (): void => {
    setTheme((currentTheme) => {
      const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
      saveAdminTheme(nextTheme);

      return nextTheme;
    });
  };

  if (authState === 'checking') {
    return <AdminLoadingPage theme={theme} />;
  }

  if (authState === 'admin' && authToken !== null && dictionaries !== null) {
    return (
      <Routes>
        <Route
          path="/admin/*"
          element={(
            <AdminWorkspace
              authToken={authToken}
              dictionaries={dictionaries}
              onLogout={handleLogout}
              onThemeToggle={toggleTheme}
              theme={theme}
            />
          )}
        />
        <Route path="*" element={<Navigate replace to="/admin" />} />
      </Routes>
    );
  }

  return (
    <AdminLoginPage
      form={loginForm}
      fieldErrors={loginFieldErrors}
      isSubmitting={isSubmitting}
      message={loginError}
      onChange={(nextForm) => {
        setLoginForm(nextForm);
        setLoginFieldErrors({});
      }}
      onSubmit={handleLogin}
      onThemeToggle={toggleTheme}
      theme={theme}
    />
  );
}

function validateAdminLogin(form: LoginRequest): FormErrors {
  const errors: FormErrors = {};

  if (!validateEmail(form.user_email)) {
    addFormError(errors, 'user_email', 'Укажите корректный email администратора.');
  }

  if (form.password === '') {
    addFormError(errors, 'password', 'Введите пароль.');
  }

  return errors;
}

function clearAdminSession(): void {
  clearAuthToken();
  setHttpToken(null);
}
